import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './GoalModals.css';

export function GoalConfirmModal({
    open,
    title = 'Are you sure?',
    message,
    confirmLabel = "Yeah, I'm sure",
    cancelLabel = 'Cancel',
    busy = false,
    error = '',
    onConfirm,
    onCancel,
}) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, busy, onCancel]);

    if (!open) return null;

    return ReactDOM.createPortal(
        <div
            className="dv2-gmodal dv2-gmodal--confirm"
            role="presentation"
            onClick={busy ? undefined : onCancel}
        >
            <div
                className="dv2-gmodal__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dv2-gmodal-confirm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h3 id="dv2-gmodal-confirm-title" className="dv2-gmodal__title">
                    {title}
                </h3>
                <p className="dv2-gmodal__msg">{message}</p>
                {error ? <p className="dv2-gmodal__error">{error}</p> : null}
                <div className="dv2-gmodal__actions">
                    <button
                        type="button"
                        className="dv2-gmodal__btn dv2-gmodal__btn--ghost"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        {cancelLabel}
                    </button>
                    <button
                        type="button"
                        className="dv2-gmodal__btn dv2-gmodal__btn--solid"
                        onClick={onConfirm}
                        disabled={busy}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

function toApiDate(value) {
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
}

export function GoalEditModal({
    open,
    goal,
    busy = false,
    error = '',
    onSave,
    onCancel,
}) {
    const [text, setText] = useState('');
    const [date, setDate] = useState(null);
    const [localError, setLocalError] = useState('');

    useEffect(() => {
        if (!open || !goal) return;
        setText(goal.text || '');
        const raw = goal.completedByDate ? new Date(goal.completedByDate) : new Date();
        setDate(Number.isNaN(raw.getTime()) ? new Date() : raw);
        setLocalError('');
    }, [open, goal]);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape' && !busy) onCancel();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, busy, onCancel]);

    if (!open || !goal) return null;

    const handleSave = () => {
        const trimmed = text.replace(/\s+/g, ' ').trim();
        if (!trimmed) {
            setLocalError('Goal text is required.');
            return;
        }
        if (!date || Number.isNaN(date.getTime())) {
            setLocalError('Please select a completion date.');
            return;
        }
        setLocalError('');
        onSave({
            id: goal.id,
            text: trimmed,
            completedByDate: toApiDate(date),
        });
    };

    return (
        <div className="dv2-gmodal" role="presentation" onClick={busy ? undefined : onCancel}>
            <div
                className="dv2-gmodal__panel dv2-gmodal__panel--edit"
                role="dialog"
                aria-modal="true"
                aria-labelledby="dv2-gmodal-edit-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="dv2-gmodal__head">
                    <h3 id="dv2-gmodal-edit-title" className="dv2-gmodal__title">
                        Edit Goal
                    </h3>
                    <button
                        type="button"
                        className="dv2-gmodal__close"
                        onClick={onCancel}
                        disabled={busy}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {goal.areaName ? (
                    <p className="dv2-gmodal__area" style={{ color: goal.color || undefined }}>
                        {goal.areaName}
                    </p>
                ) : null}

                <label className="dv2-gmodal__label" htmlFor="dv2-goal-text">
                    Goal
                </label>
                <input
                    id="dv2-goal-text"
                    className="dv2-gmodal__input"
                    type="text"
                    maxLength={300}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={busy}
                    autoFocus
                />

                <label className="dv2-gmodal__label" htmlFor="dv2-goal-date">
                    Completion date
                </label>
                <div className="dv2-gmodal__datepicker">
                    <DatePicker
                        id="dv2-goal-date"
                        selected={date}
                        onChange={(d) => setDate(d)}
                        minDate={new Date()}
                        dateFormat="MMMM d, yyyy"
                        className="dv2-gmodal__input"
                        calendarClassName="dv2-gmodal-calendar"
                        popperClassName="dv2-gmodal-popper"
                        popperPlacement="top"
                        disabled={busy}
                    />
                </div>

                {localError ? <p className="dv2-gmodal__error">{localError}</p> : null}
                {!localError && error ? (
                    <p className="dv2-gmodal__error">{error}</p>
                ) : null}

                <div className="dv2-gmodal__actions">
                    <button
                        type="button"
                        className="dv2-gmodal__btn dv2-gmodal__btn--ghost"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="dv2-gmodal__btn dv2-gmodal__btn--solid"
                        onClick={handleSave}
                        disabled={busy}
                    >
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}
